import React, { useEffect, useState, useMemo, memo, useCallback } from 'react';
import { getAllSwagger } from '../../redux-store/aiChatsReducers/aiChatsActions';
import { useDispatch } from 'react-redux';
import SwaggerDoc from 'components/Apis/SwaggerDoc';
import { BookCopy, Copy } from 'lucide-react';
import { Tooltip } from "@mui/material";
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ApiEndpointTable from 'components/ApiCollectionOverview/ApiOverview/ApiEndpointTable';
import { getMethodBadgeStyle } from 'config/constants';
import { useInView } from 'react-intersection-observer';

const LazySwagger = memo(({ doc, onCopy }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <div ref={ref} className="border-b border-gray-700">
            <div className='flex items-center justify-between'>
                <h5 className="text-lg font-semibold">{doc.name}</h5>
                <Tooltip title="Copy to clipboard">
                    <Button
                        type="button"
                        onClick={() => onCopy(doc.swagger_doc)}
                        aria-label='Copy'
                        className="w-auto h-8 bg-transparent border-none text-gray-900 px-2 py-1 m-1 !rounded shadow-none transition-all duration-150"
                    >
                        <Copy size={16} className="cursor-pointer" />
                    </Button>
                </Tooltip>
            </div>
            {inView ? (
                <SwaggerDoc swaggerDoc={doc.swagger_doc} />
            ) : (
                <div className="flex justify-center items-center h-20">
                    <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                </div>
            )}
        </div>
    );
});

LazySwagger.displayName = 'LazySwagger';

const PreviewView = memo(({ collectionId, getMethodBadgeStyle, addEntityToChatContext, isAiAgent = false, selectedChatEntityIds }) => {
    return (
        <ApiEndpointTable
            collectionId={collectionId}
            getMethodBadgeStyle={getMethodBadgeStyle}
            addEntityToChatContext={addEntityToChatContext}
            isAiAgent={isAiAgent}
            selectedChatEntityIds={selectedChatEntityIds}
        />
    );
});

PreviewView.displayName = 'PreviewView';

const CodeView = memo(({ collectionSwaggerDoc, isLoading, handleCopy }) => {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <ProgressSpinner
                    style={{ width: '40px', height: '40px' }}
                    strokeWidth="4"
                    animationDuration=".5s"
                />
            </div>
        );
    }

    if (collectionSwaggerDoc.length === 0) {
        return <p className="text-gray-500">No Swagger documents available.</p>;
    }

    return (
        <div className="space-y-4">
            {collectionSwaggerDoc.map((doc, index) => (
                <LazySwagger
                    key={doc.id || index}
                    doc={doc}
                    onCopy={handleCopy}
                />
            ))}
        </div>
    );
});

CodeView.displayName = 'CodeView';

const SwaggerScreen = ({
    collection,
    addEntityToChatContext = () => { },
    isAiAgent = false,
    selectedChatEntityIds = null,
    isProcessing = false
}) => {
    const { openSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const [collectionSwaggerDoc, setCollectionSwaggerDoc] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState("preview");

    const fetchSwagger = async () => {
        setIsLoading(true);
        try {
            const response = await dispatch(getAllSwagger({ collectionId: collection.id })).unwrap();
            setCollectionSwaggerDoc(response.data);
        } catch (error) {
            console.error('Error fetching swagger:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = useCallback(async (swaggerDoc) => {
        try {
            const jsonString = JSON.stringify(swaggerDoc, null, 2);
            await navigator.clipboard.writeText(jsonString);
            openSnackbar({ message: 'Copied To Clipboard', severity: 'info' });
        } catch (error) {
            openSnackbar({ message: 'Some Error Occurred. Could not copy Swagger Document', severity: 'error' });
            console.error('Could not copy swagger document:', error);
        }
    }, [openSnackbar]);

    useEffect(() => {
        if (collection?.id) {
            fetchSwagger();
        }
    }, [collection?.id]);

    const handleView = useMemo(() => (event, selectedView) => {
        if (selectedView === "code" && collectionSwaggerDoc.length === 0) {
            fetchSwagger();
        }
        setView(selectedView);
    }, [collection.id, collectionSwaggerDoc.length]);

    const currentView = useMemo(() => {
        if (view === "preview") {
            return (
                <PreviewView
                    collectionId={collection.id}
                    getMethodBadgeStyle={getMethodBadgeStyle}
                    addEntityToChatContext={addEntityToChatContext}
                    isAiAgent={isAiAgent}
                    selectedChatEntityIds={selectedChatEntityIds}
                />
            );
        }
        return (
            <CodeView
                collectionSwaggerDoc={collectionSwaggerDoc}
                isLoading={isLoading}
                handleCopy={handleCopy}
            />
        );
    }, [view, collectionSwaggerDoc, isLoading, handleCopy, isProcessing]);

    return (
        <>
            <div className='p-4'>
                <div className='flex justify-between items-center'>
                    <div className='flex items-center'>
                        <BookCopy /><span className="!text-2xl !font-semibold ml-3"> {collection.title}</span>
                    </div>
                    <div>
                        <ToggleButtonGroup
                            value={view}
                            exclusive
                            onChange={handleView}
                            aria-label="View"
                            className='my-2 mx-2'
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: 'var(--background-secondary)',
                                padding: '4px',
                                '& .MuiToggleButton-root': {
                                    border: 'none',
                                    borderRadius: '3px !important',
                                    margin: '0',
                                    '&.Mui-selected': {
                                        backgroundColor: 'white',
                                        color: 'var(--primary-color)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            backgroundColor: 'white'
                                        }
                                    },
                                    '&:not(.Mui-selected)': {
                                        '&:hover': {
                                            backgroundColor: 'var(--background-secondary)'
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="preview" aria-label="Preview" className='px-3 py-1'>
                                Preview
                            </ToggleButton>
                            <ToggleButton value="code" aria-label="Code" className='px-3 py-1'>
                                Code
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                </div>
                <hr />
                {currentView}
            </div>
        </>
    );
};

export default memo(SwaggerScreen);